import React from 'react';

// Shared question wrapper -- label + optional error text. No required
// marker: on these forms nearly every question is required, so a per-field
// asterisk added noise without conveying anything useful.
export const Question = ({ label, error, children }) => (
  <div className="py-5 border-b border-neutral-200 last:border-b-0">
    <label className="block text-sm font-medium text-neutral-900 mb-3">
      {label}
    </label>
    {children}
    {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
  </div>
);

// Explicit hex colors, not Tailwind utility classes -- guarantees the
// selected/unselected contrast renders correctly regardless of whatever
// Tailwind is (or isn't) compiling in this project's build pipeline.
const SELECTED_STYLE = { backgroundColor: '#2563eb', borderColor: '#2563eb', color: '#ffffff' };
const UNSELECTED_STYLE = { backgroundColor: '#ffffff', borderColor: '#cbd5e1', color: '#334155' };

// A row of clickable number buttons from min to max (inclusive), with
// labels under the leftmost/rightmost buttons -- matches how Microsoft
// Forms actually renders its 0-10 / 1-7 "scale" questions.
export const ScaleButtons = ({ value, onChange, min, max, leftLabel, rightLabel }) => {
  const options = [];
  for (let i = min; i <= max; i++) options.push(i);

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {options.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            style={value === n ? SELECTED_STYLE : UNSELECTED_STYLE}
            className="h-10 w-10 rounded border text-sm font-medium transition-colors"
          >
            {n}
          </button>
        ))}
      </div>
      {(leftLabel || rightLabel) && (
        <div className="flex justify-between mt-2 text-xs text-neutral-500">
          <span>{leftLabel}</span>
          <span>{rightLabel}</span>
        </div>
      )}
    </div>
  );
};

// PTSD/TBI/Depression/Anxiety coping grid: 5 columns (values 1-5), labeled
// at 1/3/5 as "Unable to cope" / "I cope okay" / "I don't even notice the
// symptoms" (2 and 4 are intentionally unlabeled, matching the source form).
const SYMPTOM_ROWS = [
  { key: 'ptsd', label: 'PTSD' },
  { key: 'tbi', label: 'TBI' },
  { key: 'depression', label: 'Depression' },
  { key: 'anxiety', label: 'Anxiety' },
];

const SYMPTOM_COLUMN_LABELS = {
  1: 'Unable to cope',
  3: 'I cope okay',
  5: "I don't even notice the symptoms",
};

export const SymptomScaleGrid = ({ values, onChange }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full text-sm">
      <thead>
        <tr>
          <th className="text-left font-normal text-neutral-500 pb-2"></th>
          {[1, 2, 3, 4, 5].map((col) => (
            <th key={col} className="text-center font-normal text-neutral-500 pb-2 px-2 max-w-[7rem]">
              {SYMPTOM_COLUMN_LABELS[col] || ''}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {SYMPTOM_ROWS.map((row) => (
          <tr key={row.key}>
            <td className="py-2 pr-4 text-neutral-800 whitespace-nowrap">{row.label}</td>
            {[1, 2, 3, 4, 5].map((col) => (
              <td key={col} className="text-center py-2">
                <input
                  type="radio"
                  name={`symptom-${row.key}`}
                  checked={values[row.key] === col}
                  onChange={() => onChange(row.key, col)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const HOURS_OF_SLEEP_OPTIONS = [
  { value: 1, label: 'Less than 5' },
  { value: 5, label: '5-7 hours' },
  { value: 7, label: '7-9 hours' },
  { value: 9, label: '9-12 hours' },
  { value: 12, label: 'More than 12' },
];

const SELECTED_STYLE_LIGHT = { backgroundColor: '#eff6ff', borderColor: '#60a5fa', color: '#1e40af' };

export const HoursOfSleepChoice = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-3">
    {HOURS_OF_SLEEP_OPTIONS.map((opt) => (
      <label
        key={opt.value}
        style={value === opt.value ? SELECTED_STYLE_LIGHT : UNSELECTED_STYLE}
        className="flex items-center gap-2 px-3 py-2 rounded border text-sm cursor-pointer"
      >
        <input
          type="radio"
          name="hours_of_sleep"
          checked={value === opt.value}
          onChange={() => onChange(opt.value)}
          className="h-4 w-4"
        />
        {opt.label}
      </label>
    ))}
  </div>
);

export const YesNoToggle = ({ value, onChange }) => (
  <div className="flex gap-3">
    {[{ label: 'Yes', val: true }, { label: 'No', val: false }].map((opt) => (
      <button
        key={opt.label}
        type="button"
        onClick={() => onChange(opt.val)}
        style={value === opt.val ? SELECTED_STYLE : UNSELECTED_STYLE}
        className="px-5 py-2 rounded border text-sm font-medium transition-colors"
      >
        {opt.label}
      </button>
    ))}
  </div>
);

export const SurveyTextarea = ({ value, onChange, rows = 3 }) => (
  <textarea
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    rows={rows}
    className="block w-full rounded border-neutral-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
  />
);
